import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { useAiChat } from '@factorialco/f0-react'
import { useCoAgent, useCopilotChat } from '@copilotkit/react-core'

import { useOneCreateActionState } from 'modules/core/lib/oneCreateActions'

import { AiAgentGlobalActions } from 'modules/ai/components/AiAgentGlobalActions'

export type OneSharedState<T = unknown> = {
  platform: 'web' | 'mobile'
  requiredSkills: string[]
  coCreationData: T | null
  files?: OneFile[] | null
  currentPage: {
    url: string
    title: string
  }
}

export type OneFile = {
  type: 'binary'
  url?: string
  filename?: string
  mimeType?: string
}

type OneContextValue = {
  state: OneSharedState
  setState: React.Dispatch<React.SetStateAction<OneSharedState>>
  isThinking: boolean
}

const OneContext = React.createContext<OneContextValue | null>(null)
const OneAvailableContext = React.createContext<boolean>(false)

// Test seam: some specs need to observe the full shared One state (skills/files/currentPage),
// especially for `<OneProvider enabled={false}>`, where the public `useOne` return value falls
// back to caller data and cannot prove the provider state itself was reset/synced correctly.
export const __TEST_ONLY__OneContext = OneContext

export const useOneFeatureAvailable = () => React.useContext(OneAvailableContext)

// Distinguish “first keyed initialization has not happened yet” from real caller keys.
// `coCreationKey` can legitimately be `string | number | boolean`, so a unique Symbol
// avoids collisions with user-provided values.
const UNINITIALIZED_CO_CREATION_KEY = Symbol('uninitialized-co-creation-key')

const getPageIdentity = (url: string) => {
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return url
  }
}

const getUrlWithoutHash = (url: string) => {
  try {
    const parsedUrl = new URL(url, window.location.origin)
    parsedUrl.hash = ''

    return parsedUrl.toString()
  } catch {
    return url.split('#')[0] ?? url
  }
}

const isHashOnlyNavigation = (previousUrl: string, nextUrl: string) => {
  return previousUrl !== nextUrl && getUrlWithoutHash(previousUrl) === getUrlWithoutHash(nextUrl)
}

const hasCoCreationDataValue = <T,>(value: OneSharedState['coCreationData']): value is T => {
  return value !== null
}

const isEmptyCoCreationBootstrapState = (
  state: Pick<OneSharedState, 'coCreationData' | 'files' | 'requiredSkills'>
) => {
  return state.coCreationData === null && state.files === null && state.requiredSkills.length === 0
}

const haveSameRequiredSkills = (left: string[], right: string[]) => {
  return left.length === right.length && left.every((skill, index) => skill === right[index])
}

const buildOneSharedState = <T,>({
  requiredSkills,
  coCreationData,
  files,
  currentPage,
}: {
  requiredSkills: string[]
  coCreationData: T
  files: OneFile[] | null
  currentPage: OneSharedState['currentPage']
}): OneSharedState<T> => {
  return {
    platform: 'web',
    requiredSkills,
    coCreationData,
    files,
    currentPage,
  }
}

export const resolveCoCreationInitData = <T,>({
  isFirstInitialization,
  dataFromRedirect,
  coCreationData,
}: {
  isFirstInitialization: boolean
  dataFromRedirect: T | undefined
  coCreationData: T
}) => {
  return isFirstInitialization ? (dataFromRedirect ?? coCreationData) : coCreationData
}

const isStateUpdater = <T,>(update: React.SetStateAction<T>): update is (previousState: T) => T => {
  return typeof update === 'function'
}

const useLatestStateSafeSetter = <T,>(
  state: T,
  rawSetState: (state: T) => void
): React.Dispatch<React.SetStateAction<T>> => {
  const stateRef = React.useRef(state)
  stateRef.current = state

  return React.useCallback(
    (update) => {
      const nextState = isStateUpdater(update) ? update(stateRef.current) : update

      if (nextState === stateRef.current) {
        return
      }

      stateRef.current = nextState
      rawSetState(nextState)
    },
    [rawSetState]
  )
}

const DisabledOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()

  const currentPageFromLocation = React.useMemo(
    () => ({
      url: `${window.location.origin}${location.pathname}${location.search}${location.hash ?? ''}`,
      title: document.title,
    }),
    [location.pathname, location.search, location.hash]
  )

  const [state, rawSetState] = React.useState<OneSharedState>(() => ({
    platform: 'web' as const,
    requiredSkills: [],
    coCreationData: null,
    files: null,
    currentPage: currentPageFromLocation,
  }))
  const setState = useLatestStateSafeSetter(state, rawSetState)

  React.useEffect(() => {
    setState((prev) => {
      const sameIdentity =
        getPageIdentity(prev.currentPage.url) === getPageIdentity(currentPageFromLocation.url)
      const urlOrTitleChanged =
        prev.currentPage.url !== currentPageFromLocation.url ||
        prev.currentPage.title !== currentPageFromLocation.title

      if (!urlOrTitleChanged) return prev

      if (sameIdentity) {
        return {
          ...prev,
          currentPage: currentPageFromLocation,
        }
      }

      return {
        ...prev,
        currentPage: currentPageFromLocation,
        coCreationData: null,
        files: null,
        requiredSkills: [],
      }
    })
  }, [currentPageFromLocation, setState])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking: false }
  }, [state, setState])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

const InternalOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const { setInitialMessage, setFooter, setPlaceholders, setVisualizationMode } = useAiChat()
  // Re-derive initialState when the route changes so CopilotKit's useCoAgent
  // picks up the current URL instead of a stale one captured on first mount.
  const initialState = React.useMemo<OneSharedState>(() => {
    return {
      platform: 'web',
      requiredSkills: [],
      coCreationData: null,
      files: null,
      currentPage: {
        url: window.location.href,
        title: document.title,
      },
    }
  }, [location.pathname, location.search])

  const { state: rawState, setState: rawSetState } = useCoAgent<OneSharedState>({
    name: 'one-workflow',
    initialState,
  })

  // CopilotKit 1.54.0 may return an empty or partial state object before the
  // agent connection is established. Merge with initialState to guarantee
  // all required fields (like currentPage) are always present. Memoize to
  // avoid creating a new object on every render (which would re-trigger
  // effects that depend on state and cause unnecessary context re-renders).
  const state: OneSharedState = React.useMemo(
    () => ({
      platform: rawState?.platform ?? initialState.platform,
      requiredSkills: rawState?.requiredSkills ?? initialState.requiredSkills,
      coCreationData: rawState?.coCreationData ?? initialState.coCreationData,
      files: rawState?.files ?? initialState.files,
      currentPage: {
        url: rawState?.currentPage?.url ?? initialState.currentPage.url,
        title: rawState?.currentPage?.title ?? initialState.currentPage.title,
      },
    }),
    [rawState, initialState]
  )
  const setState = useLatestStateSafeSetter(state, rawSetState)

  const { isLoading: isThinking } = useCopilotChat()

  const setInitialMessageRef = React.useRef(setInitialMessage)
  setInitialMessageRef.current = setInitialMessage
  const setFooterRef = React.useRef(setFooter)
  setFooterRef.current = setFooter
  const setPlaceholdersRef = React.useRef(setPlaceholders)
  setPlaceholdersRef.current = setPlaceholders
  const setVisualizationModeRef = React.useRef(setVisualizationMode)
  setVisualizationModeRef.current = setVisualizationMode
  const setStateRef = React.useRef(setState)
  setStateRef.current = setState
  const stateRef = React.useRef(state)
  stateRef.current = state

  React.useEffect(() => {
    const nextPage = { url: window.location.href, title: document.title }
    const previousState = stateRef.current
    const urlChanged = previousState.currentPage.url !== nextPage.url
    const titleChanged = previousState.currentPage.title !== nextPage.title

    if (!urlChanged && !titleChanged) {
      return
    }

    if (!titleChanged && isHashOnlyNavigation(previousState.currentPage.url, nextPage.url)) {
      return
    }

    const sameIdentity =
      getPageIdentity(previousState.currentPage.url) === getPageIdentity(nextPage.url)

    setStateRef.current((prev) => {
      const prevUrlChanged = prev.currentPage.url !== nextPage.url
      const prevTitleChanged = prev.currentPage.title !== nextPage.title

      if (!prevUrlChanged && !prevTitleChanged) {
        return prev
      }

      if (!prevTitleChanged && isHashOnlyNavigation(prev.currentPage.url, nextPage.url)) {
        return prev
      }

      const prevSameIdentity =
        getPageIdentity(prev.currentPage.url) === getPageIdentity(nextPage.url)

      if (prevUrlChanged && !prevSameIdentity) {
        return {
          ...prev,
          currentPage: nextPage,
          coCreationData: null,
          files: null,
          requiredSkills: [],
        }
      }

      return { ...prev, currentPage: nextPage }
    })

    if (urlChanged && !sameIdentity) {
      setInitialMessageRef.current(undefined)
      setFooterRef.current(undefined)
      setPlaceholdersRef.current([])
      setVisualizationModeRef.current('sidepanel')
    }
  }, [location.key])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking }
  }, [state, setState, isThinking])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

type UseOneResult<T> = {
  coCreationData: T
  setCoCreationData: (data: T) => void
  isThinking: boolean
}

export type UseOneOptions = {
  /** When false, skips initializing the shared state from this hook instance. */
  enabled?: boolean
  /** When true, opens the AI chat panel when the component mounts. Only has effect when used inside the layout that provides the chat (e.g. FactorialOneLayout). */
  autoOpenChat?: boolean
  /** When set, this message is shown as the initial user message in the chat when the panel opens (e.g. when using autoOpenChat). */
  initialMessage?: string
  /** When true, opens the chat in fullscreen mode (uses setVisualizationMode). Only has effect when autoOpenChat is true. */
  fullscreen?: boolean
  /** Optional footer content (e.g. action buttons) rendered below the chat textarea. Cleared on URL change. */
  footer?: React.ReactNode
  /** Optional placeholder strings shown as suggestions in the chat input. Cleared on URL change. */
  placeholders?: string[]
  /** Optional files passed to One alongside co-creation data. */
  files?: OneFile[] | null
  /** Stable key so the shared co-creation state is re-initialized when the active resource changes. When omitted, useOne keeps the existing bootstrap-once behavior. */
  coCreationKey?: string | number | boolean
  /** Stable key so the effect re-runs when footer intent changes (e.g. pass `canCreateSurvey`). Avoids listing `footer` in deps and prevents update loops. */
  footerKey?: string | number | boolean
  /** Stable key so the effect re-runs when placeholders intent changes (e.g. pass `placeholders.length`). */
  placeholdersKey?: string | number
}

export const useOne = <T,>(
  requiredSkills: string[],
  coCreationData: T,
  options?: UseOneOptions
): UseOneResult<T> => {
  const ctx = React.useContext(OneContext)
  if (!ctx) {
    throw new Error('useOne must be used within OneProvider')
  }

  const ctxRef = React.useRef(ctx)
  ctxRef.current = ctx

  const initializedCoCreationKeyRef = React.useRef<
    typeof UNINITIALIZED_CO_CREATION_KEY | UseOneOptions['coCreationKey']
  >(UNINITIALIZED_CO_CREATION_KEY)
  const initializedNonKeyedPageRef = React.useRef<{
    pageIdentity: string | null
    initialized: boolean
  }>({ pageIdentity: null, initialized: false })
  const enabled = options?.enabled !== false
  const hasCoCreationKey = options?.coCreationKey !== undefined
  const {
    setOpen: setChatOpen,
    setInitialMessage,
    setVisualizationMode,
    setFooter,
    setPlaceholders,
  } = useAiChat()
  const { data: dataFromRedirect } = useOneCreateActionState<T>()

  const setChatOpenRef = React.useRef(setChatOpen)
  setChatOpenRef.current = setChatOpen
  const setInitialMessageRef = React.useRef(setInitialMessage)
  setInitialMessageRef.current = setInitialMessage
  const setVisualizationModeRef = React.useRef(setVisualizationMode)
  setVisualizationModeRef.current = setVisualizationMode
  const setFooterRef = React.useRef(setFooter)
  setFooterRef.current = setFooter
  const setPlaceholdersRef = React.useRef(setPlaceholders)
  setPlaceholdersRef.current = setPlaceholders

  const requiredSkillsKey = JSON.stringify(requiredSkills)
  const stableRequiredSkills = React.useMemo(() => requiredSkills, [requiredSkillsKey])

  React.useEffect(() => {
    if (!hasCoCreationKey) {
      initializedCoCreationKeyRef.current = UNINITIALIZED_CO_CREATION_KEY
      return
    }

    initializedNonKeyedPageRef.current = { pageIdentity: null, initialized: false }
  }, [hasCoCreationKey])

  React.useEffect(() => {
    if (!enabled || !options?.autoOpenChat) {
      return
    }

    setVisualizationModeRef.current(options.fullscreen ? 'fullscreen' : 'sidepanel')
    if (options.initialMessage !== undefined) {
      setInitialMessageRef.current(options.initialMessage)
    }
    setChatOpenRef.current(true)
  }, [enabled, options?.autoOpenChat, options?.fullscreen, options?.initialMessage])

  const footerDep = options?.footerKey !== undefined ? options.footerKey : options?.footer
  React.useEffect(() => {
    if (!enabled) {
      return
    }

    setFooterRef.current(options?.footer ?? undefined)
  }, [enabled, footerDep])

  const placeholdersDep =
    options?.placeholdersKey !== undefined ? options.placeholdersKey : options?.placeholders
  React.useEffect(() => {
    if (!enabled) {
      return
    }

    setPlaceholdersRef.current(options?.placeholders ?? [])
  }, [enabled, placeholdersDep])

  React.useEffect(() => {
    if (!enabled) {
      return
    }

    const currentPage = { url: window.location.href, title: document.title }
    const nextFiles = options?.files ?? null

    if (!hasCoCreationKey) {
      const currentPageIdentity = getPageIdentity(currentPage.url)

      if (initializedNonKeyedPageRef.current.pageIdentity !== currentPageIdentity) {
        const sharedStateBelongsToCurrentPage =
          getPageIdentity(ctxRef.current.state.currentPage.url) === currentPageIdentity

        initializedNonKeyedPageRef.current = {
          pageIdentity: currentPageIdentity,
          initialized:
            sharedStateBelongsToCurrentPage &&
            !isEmptyCoCreationBootstrapState(ctxRef.current.state),
        }
      }

      if (initializedNonKeyedPageRef.current.initialized) {
        return
      }

      initializedNonKeyedPageRef.current = {
        pageIdentity: currentPageIdentity,
        initialized: true,
      }

      const nextCoCreationData = dataFromRedirect ?? coCreationData

      ctxRef.current.setState((prev) => {
        const prevPageIdentity = getPageIdentity(prev.currentPage.url)

        if (prevPageIdentity === currentPageIdentity && !isEmptyCoCreationBootstrapState(prev)) {
          return prev
        }

        return buildOneSharedState({
          requiredSkills: stableRequiredSkills,
          coCreationData: nextCoCreationData,
          files: nextFiles,
          currentPage,
        })
      })
      return
    }

    const isFirstInit = initializedCoCreationKeyRef.current === UNINITIALIZED_CO_CREATION_KEY
    const keyChanged = !isFirstInit && initializedCoCreationKeyRef.current !== options.coCreationKey

    if (!isFirstInit && !keyChanged) {
      return
    }

    initializedCoCreationKeyRef.current = options.coCreationKey

    const nextCoCreationData = resolveCoCreationInitData({
      isFirstInitialization: isFirstInit,
      dataFromRedirect,
      coCreationData,
    })

    ctxRef.current.setState(() => {
      return buildOneSharedState({
        requiredSkills: stableRequiredSkills,
        coCreationData: nextCoCreationData,
        files: nextFiles,
        currentPage,
      })
    })
  }, [
    enabled,
    coCreationData,
    stableRequiredSkills,
    dataFromRedirect,
    hasCoCreationKey,
    options?.coCreationKey,
    options?.files,
  ])

  React.useEffect(() => {
    if (!enabled) {
      return
    }

    const nextFiles = options?.files ?? null

    if (ctxRef.current.state.files === nextFiles) {
      return
    }

    ctxRef.current.setState((prev) => {
      if (prev.files === nextFiles) return prev

      return { ...prev, files: nextFiles }
    })
  }, [enabled, options?.files])

  // Skills-sync: re-assert the caller's requiredSkills whenever the shared
  // state diverges.  CopilotKit's useCoAgent may overwrite requiredSkills
  // (e.g. via onRunInitialized server sync) after this hook already set them.
  // Including ctx.state.requiredSkills in deps makes this effect reactive to
  // such external overwrites so it can correct them immediately.
  React.useEffect(() => {
    if (!enabled) {
      return
    }

    if (haveSameRequiredSkills(ctxRef.current.state.requiredSkills, stableRequiredSkills)) {
      return
    }

    ctxRef.current.setState((prev) => {
      if (haveSameRequiredSkills(prev.requiredSkills, stableRequiredSkills)) {
        return prev
      }

      return { ...prev, requiredSkills: stableRequiredSkills }
    })
  }, [enabled, stableRequiredSkills, ctx.state.requiredSkills])

  // CoCreation-sync: when a component (e.g. FormEditor on an edit page) provides
  // non-null coCreationData but the shared state still has null, push it in.
  // This covers the case where the initialization effect was skipped because
  // requiredSkills were already set by a prior useOne consumer or by the
  // skills-sync effect, making isEmptyCoCreationBootstrapState return false
  // and preventing the full state bootstrap. Including ctx.state.coCreationData
  // in deps also handles CopilotKit overwriting it back to null.
  React.useEffect(() => {
    if (!enabled) return
    if (coCreationData === null || coCreationData === undefined) return
    if (ctxRef.current.state.coCreationData !== null) return

    ctxRef.current.setState((prev) => {
      if (prev.coCreationData !== null) return prev
      return { ...prev, coCreationData }
    })
  }, [enabled, coCreationData, ctx.state.coCreationData])

  const setCoCreationData = React.useCallback((data: T) => {
    ctxRef.current.setState((prev) => {
      if (prev.coCreationData === data) {
        return prev
      }

      return { ...prev, coCreationData: data }
    })
  }, [])

  return {
    coCreationData: hasCoCreationDataValue<T>(ctx.state.coCreationData)
      ? ctx.state.coCreationData
      : coCreationData,
    setCoCreationData,
    isThinking: ctx.isThinking,
  }
}

export const OneProvider = ({
  children,
  enabled,
  available = false,
}: {
  children: React.ReactNode
  enabled: boolean
  available?: boolean
}) => {
  const inner = enabled ? (
    <InternalOneProvider>
      <AiAgentGlobalActions />
      {children}
    </InternalOneProvider>
  ) : (
    <DisabledOneProvider>{children}</DisabledOneProvider>
  )

  return <OneAvailableContext.Provider value={available}>{inner}</OneAvailableContext.Provider>
}
